/**
 * Admin Operations Center — section taxonomy
 * ==========================================
 *
 * Single source of truth for the new 5-section navigation. Every existing
 * admin surface lives under exactly one section, accessible via the section's
 * sub-tab strip. Old flat-nav paths still resolve via redirect entries.
 */

import {
  Activity, FileSearch, Users, Shield, Settings,
  FolderOpen, Radio, Cpu, MessageSquareWarning, BarChart3, Link2,
  EyeOff, IdCard, BookOpen, KeyRound, LifeBuoy, History, Globe,
  ShieldCheck, Mail, ImageOff, ImagePlus, Gauge,
} from "lucide-react";

/* ---------- 5 top-level sections ---------- */
export const ADMIN_SECTIONS = [
  {
    key: "command",
    to: "/admin/command",
    label: "Command Center",
    icon: Activity,
    testid: "admin-section-command",
    short: "command",
  },
  {
    key: "dossiers",
    to: "/admin/dossiers",
    label: "Dossiers",
    icon: FileSearch,
    testid: "admin-section-dossiers",
    short: "dossiers",
  },
  {
    key: "members",
    to: "/admin/members",
    label: "Members",
    icon: Users,
    testid: "admin-section-members",
    short: "members",
  },
  {
    key: "security",
    to: "/admin/security",
    label: "Security",
    icon: Shield,
    testid: "admin-section-security",
    short: "security",
  },
  {
    key: "settings",
    to: "/admin/settings",
    label: "Settings",
    icon: Settings,
    testid: "admin-section-settings",
    short: "settings",
  },
];

/* ---------- Section sub-tabs ---------- */
// Each tab maps to a `to` (the actual route the existing components live at)
// PLUS a `mountedAt` flag indicating which legacy path renders the same UI.
// We keep the existing components intact and just point new tabs at them.

export const SECTION_TABS = {
  command: [
    { to: "/admin/command", label: "Overview", testid: "tab-cmd-overview" },
    { to: "/admin/command/retrieval", label: "Retrieval Health", testid: "tab-cmd-retrieval", icon: ShieldCheck },
  ],
  dossiers: [
    { to: "/admin/dossiers/submissions", label: "Submissions",        testid: "tab-dos-submissions", group: "Archive" },
    { to: "/admin/dossiers/review",      label: "Review",             testid: "tab-dos-review", group: "Archive" },
    { to: "/admin/dossiers/epstein",     label: "Epstein Files",      testid: "tab-dos-epstein",    icon: FolderOpen, group: "Archive" },
    { to: "/admin/dossiers/sources",     label: "Sources",            testid: "tab-dos-sources",    icon: Link2, group: "Archive" },
    { to: "/admin/dossiers/asset-directory", label: "Asset Directory", testid: "tab-dos-assets",    icon: IdCard, group: "Archive" },
    { to: "/admin/editorial",           label: "Editorial Desk",     testid: "tab-dos-editorial",  icon: Gauge, group: "Editorial" },
    { to: "/admin/dossiers/hero-review", label: "Hero Review",        testid: "tab-dos-hero-review", icon: ImageOff, group: "Editorial" },
    { to: "/admin/dossiers/profile-review", label: "Profile Review",  testid: "tab-dos-profile-review", icon: MessageSquareWarning, group: "Editorial" },
    { to: "/admin/dossiers/pullback",    label: "Pullback Console",   testid: "tab-dos-pullback",   icon: EyeOff, group: "Editorial" },
    { to: "/admin/media",               label: "Media Library",     testid: "tab-dos-media",      icon: ImagePlus, group: "Media" },
    { to: "/admin/dossiers/source-health", label: "Source Health",    testid: "tab-dos-source-health", icon: Link2, group: "Health" },
    { to: "/admin/dossiers/network-health", label: "Network Health",  testid: "tab-dos-network-health", icon: Activity, group: "Health" },
    { to: "/admin/dossiers/news-radar",  label: "News Radar",         testid: "tab-dos-radar",      icon: Radio, group: "Health" },
    { to: "/admin/dossiers/automation",  label: "Automation",         testid: "tab-dos-automation", icon: Cpu, group: "Health" },
  ],
  members: [
    { to: "/admin/members/audience",   label: "Audience",     testid: "tab-mem-audience" },
    { to: "/admin/members/live",       label: "Live",         testid: "tab-mem-live",       icon: Radio },
    { to: "/admin/members/reach",      label: "Global Reach", testid: "tab-mem-reach",      icon: Globe },
    { to: "/admin/members/comments",   label: "Comments",     testid: "tab-mem-comments",   icon: MessageSquareWarning },
    { to: "/admin/members/analytics",  label: "Analytics",    testid: "tab-mem-analytics",  icon: BarChart3 },
  ],
  security: [
    { to: "/admin/security/center",        label: "Security Center", testid: "tab-sec-center",   icon: Shield, ownerOnly: true, group: "Access" },
    { to: "/admin/security/failed-access", label: "Failed Access", testid: "tab-sec-failed",     ownerOnly: true, group: "Access" },
    { to: "/admin/security/recovery",      label: "Recovery",      testid: "tab-sec-recovery",   icon: LifeBuoy, group: "Access" },
    { to: "/admin/security/totp",          label: "TOTP / 2FA",    testid: "tab-sec-totp",       icon: ShieldCheck, group: "Access" },
    { to: "/admin/security/oracle",        label: "Oracle History",  testid: "tab-sec-oracle",   icon: Shield, group: "Oracle" },
    { to: "/admin/security/oracle-events", label: "Oracle Security", testid: "tab-sec-oracle-events", icon: ShieldCheck, ownerOnly: true, group: "Oracle" },
    { to: "/admin/security/audit",         label: "Audit Logs",    testid: "tab-sec-audit",      icon: History, group: "Records" },
    { to: "/admin/security/privacy",       label: "Privacy",       testid: "tab-sec-privacy",    icon: EyeOff, ownerOnly: true, group: "Records" },
    { to: "/admin/security/dispatch",      label: "Dispatch",      testid: "tab-sec-dispatch",   icon: Mail, ownerOnly: true, group: "Records" },
    { to: "/admin/security/jericho",       label: "Jericho",       testid: "tab-sec-jericho",    icon: KeyRound, group: "Records" },
  ],
  settings: [
    { to: "/admin/settings/site",  label: "Site Settings",      testid: "tab-set-site" },
    { to: "/admin/settings/users", label: "Users & Permissions", testid: "tab-set-users", ownerOnly: true },
    { to: "/admin/settings/email", label: "Email",               testid: "tab-set-email", icon: Mail },
  ],
};

/* ---------- Legacy path → new path redirects ---------- */
// All deep-link bookmarks from before the refactor continue to work.
export const LEGACY_REDIRECTS = [
  ["/admin",                     "/admin/command"],
  ["/admin/submissions",         "/admin/dossiers/submissions"],
  ["/admin/review",              "/admin/dossiers/review"],
  ["/admin/news-radar",          "/admin/dossiers/news-radar"],
  ["/admin/automation",          "/admin/dossiers/automation"],
  ["/admin/epstein",             "/admin/dossiers/epstein"],
  ["/admin/pullback",            "/admin/dossiers/pullback"],
  ["/admin/asset-directory",     "/admin/dossiers/asset-directory"],
  ["/admin/sources",             "/admin/dossiers/sources"],
  ["/admin/audience",            "/admin/members/audience"],
  ["/admin/live",                "/admin/members/live"],
  ["/admin/reach-globe",         "/admin/members/reach"],
  ["/admin/comments",            "/admin/members/comments"],
  ["/admin/analytics",           "/admin/members/analytics"],
  ["/admin/audit",               "/admin/security/audit"],
  ["/admin/jericho",             "/admin/security/jericho"],
  ["/admin/recovery",            "/admin/security/recovery"],
  ["/admin/users",               "/admin/settings/users"],
  ["/admin/permissions",         "/admin/settings/users"],
];

/* Map a legacy path to its new home (returns null if no redirect needed). */
export const resolveLegacyPath = (path) => {
  for (const [from, to] of LEGACY_REDIRECTS) {
    if (path === from) return to;
  }
  return null;
};

/* Which section does this path belong to? */
export const sectionForPath = (path) => {
  if (path.startsWith("/admin/command")) return "command";
  if (path.startsWith("/admin/media")) return "dossiers";
  if (path.startsWith("/admin/dossiers")) return "dossiers";
  if (path.startsWith("/admin/members")) return "members";
  if (path.startsWith("/admin/security")) return "security";
  if (path.startsWith("/admin/settings")) return "settings";
  // Fallback: legacy paths
  const newPath = resolveLegacyPath(path);
  if (newPath) return sectionForPath(newPath);
  return "command";
};
